import { WidgetTypes as MxnWidgetTypes } from "@swiggy-private/dls-react-mxn/dist/WidgetController/constants";
import {
    GridWidgetTypes,
    type IGridWidgetProps,
} from "@swiggy-private/dls-react-mxn/dist/WidgetController/widgets/GridWidgetLayout/type";
import type { CrossSellItemInfo } from "@swiggy-private/dls-react-mxn/dist/WidgetController/widgets/ItemCetagory/type";
import { GridType } from "@swiggy-private/dls-web-core/dist/components/GridContainer/types";
import type { CrossSellConfig, ILocalCart } from "@swiggy-private/portal-dineout-context";
import {
    CROSS_SELL_BACKGROUND,
    CROSS_SELL_CAROUSEL_CONFIG,
    CROSS_SELL_DISH_CARD_FIELD_DEFAULTS,
    CROSS_SELL_GRID_TYPOGRAPHY,
    CROSS_SELL_HEADER_DEFAULTS,
} from "./constants";
import type { CrossSellNormalItem } from "./types";
import { getCartItemFromLocalCart } from "../../../utils";

const buildCrossSellCarouselItems = (
    displayItems: CrossSellNormalItem[],
    localCart: ILocalCart | undefined
): CrossSellItemInfo[] => {
    const defaults = CROSS_SELL_DISH_CARD_FIELD_DEFAULTS;
    return displayItems.map((info) => {
        const idStr = String(info.id ?? "");
        const aggregated = info.ratings?.aggregatedRating;
        const hasAggregatedRatings = Boolean(aggregated?.rating && aggregated?.ratingCount);
        return {
            analytics: info.analytics,
            info: {
                id: idStr,
                name: info.name ?? "",
                description: info.description ?? "",
                price: info.price ?? info.finalPrice ?? 0,
                finalPrice: info.finalPrice ?? info.price ?? 0,
                isVeg: info.isVeg ?? defaults.DEFAULT_IS_VEG,
                imageId: info.imageId,
                vegType: info.vegType ?? defaults.DEFAULT_VEG_TYPE,
                isBestseller: info.isBestseller ?? defaults.DEFAULT_IS_BESTSELLER,
                addons: info?.addons,
                variants: info?.variants,
                variantsV2: info?.variantsV2,
                itemAttribute: {
                    vegClassifier:
                        info?.itemAttribute?.vegClassifier ?? defaults.DEFAULT_VEG_CLASSIFIER,
                },
                ...(hasAggregatedRatings && aggregated
                    ? {
                          ratings: {
                              aggregatedRating: {
                                  rating: aggregated.rating as string,
                                  ratingCount: aggregated.ratingCount as string,
                                  ratingCountV2:
                                      aggregated.ratingCountV2 ??
                                      defaults.EMPTY_AGGREGATED_RATING.RATING_COUNT_V2,
                              },
                          },
                      }
                    : {}),
                cartItem:
                    localCart !== undefined
                        ? getCartItemFromLocalCart(idStr, localCart)
                        : undefined,
            },
        };
    });
};

export const buildCrossSellGridWidgetProps = (params: {
    displayItems: CrossSellNormalItem[];
    localCart: ILocalCart | undefined;
    crossSellConfig?: CrossSellConfig;
}): IGridWidgetProps => {
    const { displayItems, localCart, crossSellConfig } = params;
    return {
        "@type": MxnWidgetTypes.GRID_LAYOUT,
        id: "cross_sell_dish",
        showArrow: false,
        layout: {
            rows: 1,
            columns: displayItems.length,
            horizontalScrollEnabled: true,
            scrollBar: {},
            widgetTheme: { defaultMode: { backgroundColour: CROSS_SELL_BACKGROUND } },
            widgetPadding: { left: 16 },
            customStyle: {
                marginLeft: "0",
            },
        },
        gridElements: {
            infoWithStyle: {
                "@type": GridWidgetTypes.CROSS_SELL_DISH_CARD,
                crossSellItems: buildCrossSellCarouselItems(displayItems, localCart),
            },
        },
        header: {
            title:
                crossSellConfig?.title_v2?.info ??
                crossSellConfig?.title ??
                CROSS_SELL_HEADER_DEFAULTS.FALLBACK_TITLE,
            subtitle:
                crossSellConfig?.description_v2?.info ??
                CROSS_SELL_HEADER_DEFAULTS.FALLBACK_SUBTITLE,
            headerImageId: CROSS_SELL_HEADER_DEFAULTS.HEADER_IMAGE_ID,
            headerStyling: CROSS_SELL_HEADER_DEFAULTS.HEADER_STYLING,
            headerImageSize: CROSS_SELL_HEADER_DEFAULTS.HEADER_IMAGE_SIZE,
        },
        ...CROSS_SELL_GRID_TYPOGRAPHY,
        carouselConfig: CROSS_SELL_CAROUSEL_CONFIG,
        gridType: GridType.CAROUSEL,
    };
};
